import React from 'react'

import {
  AlignItems,
  Body2,
  Body3,
  Box,
  Color,
  Display,
  FlexDirection,
  Heading4,
  Icon,
  IconVariant,
  Radio,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type { LandingExperienceOption } from '@library/api-customer-engagement-experience'

import { splitTagBySubstrings } from './tagStyling'

type OptionCardTagProps = {
  label: string
  highlighted?: boolean
  strongText?: string[]
}

type OptionCardProps = {
  option: LandingExperienceOption
  isSelected: boolean
  onSelect: () => void
}

const OptionCardWrapper = withCitrusTheme(Box, () => ({
  borderRadius: '8px',
  transition: 'box-shadow 150ms ease-in-out',
  '& > label': {
    borderRadius: '8px',
    padding: '16px 12px',
  },
  '&:hover': {
    boxShadow: '0 12px 16px -4px rgba(33, 41, 48, 0.06), 0 4px 6px -2px rgba(33, 41, 48, 0.03)',
  },
}))

const StrongTagSegment = withCitrusTheme('span', (theme) => ({
  fontWeight: 700,
  color: theme.color(Color.Success_900),
}))

const OptionCardTag = ({ label, highlighted, strongText }: OptionCardTagProps) => {
  let offset = 0
  const segments = splitTagBySubstrings(label, strongText).map((segment) => {
    const key = `${label}-${offset}`
    offset += segment.text.length

    return segment.strong ? (
      <StrongTagSegment key={key}>{segment.text}</StrongTagSegment>
    ) : (
      <span key={key} style={{ fontWeight: 400 }}>
        {segment.text}
      </span>
    )
  })

  return (
    <Box
      bg={highlighted ? Color.Success_50 : Color.NeutralGrey_50}
      borderRadius={1}
      paddingV={0.5}
      paddingH={2}
      display={Display.Flex}
      alignItems={AlignItems.Center}
    >
      <Icon name="tick" variant={IconVariant.Confirmation} size={4} />
      <Space direction="horizontal" size={1} />
      <Body3 size={[0, null, 1]} color={Color.ColdGrey_800}>
        {segments}
      </Body3>
    </Box>
  )
}

export const OptionCard = ({ option, isSelected, onSelect }: OptionCardProps) => {
  const summaryId = `gousto-anytime-option-${option.id}-summary`
  const tagsId = `gousto-anytime-option-${option.id}-tags`

  return (
    <OptionCardWrapper data-testid={`option-${option.id}`}>
      <Radio
        id={`gousto-anytime-option-${option.id}`}
        name="gousto-anytime-option"
        checked={isSelected}
        onChange={onSelect}
        alignItems={AlignItems.FlexStart}
        outline
        aria-describedby={`${summaryId} ${tagsId}`}
      >
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={3}>
          <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={1}>
            <Heading4 color={Color.ColdGrey_900} size={[4, null, 5]}>
              {option.title}
            </Heading4>
            <Body2 id={summaryId} color={Color.ColdGrey_700} size={[2, null, 4]}>
              {option.summary}
            </Body2>
          </Box>
          <Box
            id={tagsId}
            display={Display.Flex}
            flexDirection={FlexDirection.Row}
            gap={2}
            style={{ flexWrap: 'wrap' }}
          >
            {option.tags.map((tag, index) => {
              const tagStyle = option.tag_styles?.find((style) => style.index === index)

              return (
                <OptionCardTag
                  key={tag}
                  label={tag}
                  highlighted={tagStyle?.highlighted}
                  strongText={tagStyle?.strong_text}
                />
              )
            })}
          </Box>
        </Box>
      </Radio>
    </OptionCardWrapper>
  )
}
